import type {
  EmailPasswordSignUpParams,
  ForgotPasswordParams,
  GoogleSignInParams,
  LoginParams,
  ResetPasswordParams,
  SendEmailVerificationCodeParams,
  VerifyEmailParams,
  VerifyTwoFactorParams
} from './schema';
import {
  GoogleSignInResponseSchema,
  EmailPasswordSignUpResponseSchema,
  AccessTokenRefreshResponseSchema,
  ForgotPasswordResponseSchema,
  LoginResponseSchema,
  LogoutResponseSchema,
  ResetPasswordResponseSchema,
  SendEmailVerificationCodeResponseSchema,
  VerifyEmailResponseSchema,
  VerifyTwoFactorResponseSchema
} from './schema';
import { HTTP_METHOD } from '../../consts';
import { serializeRequestBody } from '../../serializer';
import type { Transport, ApiCallOptions } from '../../types';

export const createAuthApi = (transport: Transport) => ({
  /** Authenticate with email and password credentials. */
  login: (params: LoginParams, options?: ApiCallOptions) =>
    transport('/auth/v1/login', {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody(params),
      deserializer: LoginResponseSchema,
      signal: options?.signal
    }),
  emailPasswordSignUp: (params: EmailPasswordSignUpParams, options?: ApiCallOptions) =>
    transport('/auth/v1/register', {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody(params),
      deserializer: EmailPasswordSignUpResponseSchema,
      signal: options?.signal,
      includeStatus: true
    }),
  googleSignIn: ({ token, meta }: GoogleSignInParams, options?: ApiCallOptions) =>
    transport('/auth/v1/oauth/google', {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody({ accessToken: token, ...(meta && { meta }) }),
      deserializer: GoogleSignInResponseSchema,
      signal: options?.signal,
      includeStatus: true
    }),
  /** Exchange a refresh token for a new access token via httpOnly cookie rotation. */
  refreshAccessToken: (options?: ApiCallOptions) =>
    transport('/auth/v1/jwt/access-token-refresh', {
      method: HTTP_METHOD.POST,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      deserializer: AccessTokenRefreshResponseSchema,
      signal: options?.signal
    }),
  /** Revoke the refresh token server-side via httpOnly cookie. */
  logout: (options?: ApiCallOptions) =>
    transport('/auth/v1/logout', {
      method: HTTP_METHOD.POST,
      deserializer: LogoutResponseSchema,
      signal: options?.signal
    }),
  /** Request a password-reset email for the given account. */
  forgotPassword: (params: ForgotPasswordParams, options?: ApiCallOptions) =>
    transport('/auth/v1/forgot-password', {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody(params),
      deserializer: ForgotPasswordResponseSchema,
      signal: options?.signal
    }),
  /** Reset the user's password using the reset code from the password-reset email. */
  resetPassword: (params: ResetPasswordParams, options?: ApiCallOptions) =>
    transport('/auth/v1/reset-password', {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody(params),
      deserializer: ResetPasswordResponseSchema,
      signal: options?.signal
    }),
  /** Email a verification code to the given address. Throttled per email address. */
  sendEmailVerificationCode: (params: SendEmailVerificationCodeParams, options?: ApiCallOptions) =>
    transport('/auth/v1/email-verification/send', {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody(params),
      deserializer: SendEmailVerificationCodeResponseSchema,
      signal: options?.signal
    }),
  /**
   * Finish a login that needs two-factor authentication: send the challenge from `login` with the code
   * from the authenticator app or a backup code. Unauthenticated, so a 401 never triggers a token refresh.
   */
  verifyTwoFactor: (params: VerifyTwoFactorParams, options?: ApiCallOptions) =>
    transport('/auth/v1/2fa/verify', {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody(params),
      deserializer: VerifyTwoFactorResponseSchema,
      signal: options?.signal
    }),
  /** Confirm an email address with the code from the verification email. */
  verifyEmail: (params: VerifyEmailParams, options?: ApiCallOptions) =>
    transport('/auth/v1/email-verification/verify', {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody(params),
      deserializer: VerifyEmailResponseSchema,
      signal: options?.signal
    })
});

export type AuthApi = ReturnType<typeof createAuthApi>;
